import React from "react";
import Navbar from "./Navbar";
import Reveal from "./Reveal";
import ContactForm from "./ContactForm";
import {
  portrait,
  hero,
  philosophy,
  story,
  building,
  work,
} from "../data/content";

/* Small uppercase spec label */
const Label = ({ children, className = "" }) => (
  <span className={`text-[10px] md:text-[11px] tracking-[0.24em] uppercase text-neutral-400 ${className}`}>
    {children}
  </span>
);

const Home = () => {
  return (
    <div id="top" className="bg-[#f6f5f2] text-neutral-950">
      <Navbar />

      {/* HERO — centered single column, colour portrait */}
      <section className="px-5 md:px-8 pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="mx-auto max-w-2xl text-center flex flex-col items-center">
          <Reveal className="w-full flex justify-center">
            <div className="relative w-[280px] sm:w-[340px] md:w-[400px] overflow-hidden">
              <img
                src={portrait}
                alt="Kamel Jaber"
                className="portrait-img w-full aspect-[3/4] object-cover object-center grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display leading-[1.15] mt-12 text-[26px] sm:text-[30px] md:text-[34px]">
              well-being <span className="italic">to</span> well-doing
              <span className="text-neutral-400 text-[0.5em] align-top ml-0.5">*</span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-6 max-w-md text-[14px] md:text-[15px] leading-[1.7] text-neutral-500">
              {hero.roles}
            </p>
          </Reveal>
          <Reveal delay={300}>
            <ul className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2">
              {hero.stats.map((s) => (
                <Label key={s}>{s}</Label>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section id="philosophy" className="px-5 md:px-8 py-20 md:py-28 border-t border-neutral-300">
        <div className="mx-auto max-w-2xl text-center">
          <Reveal>
            <Label>01 — Philosophy</Label>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display leading-[1.25] mt-6 text-[20px] md:text-[24px]">
              {philosophy.headline}{" "}
              <span className="italic text-neutral-500">{philosophy.emphasis}</span>
            </h2>
          </Reveal>
          <div className="mt-10 space-y-5 text-left">
            {philosophy.paragraphs.map((p, i) => (
              <Reveal key={p.slice(0, 40)} delay={i * 60}>
                <p className="text-[15px] md:text-[16px] leading-[1.75] text-neutral-700">{p}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={220}>
            <p className="mt-10 font-display text-[18px] md:text-[22px] leading-[1.35]">
              {philosophy.closing}
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="px-5 md:px-8 py-20 md:py-28 border-t border-neutral-300">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <Label className="block text-center">02 — The path here</Label>
          </Reveal>
          <div className="mt-10 space-y-6">
            {story.map((p, i) => (
              <Reveal key={p.slice(0, 40)} delay={i * 30}>
                <p
                  className={
                    i === story.length - 1
                      ? "font-display text-[18px] md:text-[22px] leading-[1.35] text-center pt-3"
                      : "text-[15px] md:text-[16px] leading-[1.75] text-neutral-700"
                  }
                >
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BUILDING */}
      <section id="building" className="px-5 md:px-8 py-20 md:py-28 border-t border-neutral-300">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <Label className="block text-center">03 — What i am building</Label>
          </Reveal>
          <div className="mt-12 divide-y divide-neutral-300 border-y border-neutral-300">
            {building.map((b, i) => (
              <Reveal key={b.name} delay={i * 60}>
                <div className="py-9 text-center">
                  <h3 className="font-display text-[20px] md:text-[24px] leading-none">{b.name}</h3>
                  <p className="mt-4 mx-auto max-w-lg text-[15px] md:text-[16px] leading-[1.7] text-neutral-600">
                    {b.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WORK */}
      <section id="work" className="px-5 md:px-8 py-20 md:py-28 border-t border-neutral-300">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <h2 className="font-display text-[24px] md:text-[30px] leading-none text-center">
              work with me
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <Label className="block text-center mt-3">Three ways</Label>
          </Reveal>

          <div className="mt-14 space-y-14">
            {work.map((w, i) => (
              <Reveal key={w.title} delay={i * 50}>
                <div className="text-center">
                  <Label>0{i + 1} — {w.tagline}</Label>
                  <h3 className="font-display text-[20px] md:text-[24px] leading-none mt-3">
                    {w.title}
                  </h3>
                  <div className="mt-5 space-y-4 mx-auto max-w-lg text-left">
                    {w.paragraphs.map((p) => (
                      <p key={p.slice(0, 40)} className="text-[15px] md:text-[16px] leading-[1.7] text-neutral-700">
                        {p}
                      </p>
                    ))}
                    {w.points && (
                      <div className="pt-5 mt-3 border-t border-neutral-200 space-y-4">
                        {w.points.map((pt) => (
                          <div key={pt.n} className="flex gap-5">
                            <Label className="pt-1 shrink-0">{pt.n}</Label>
                            <p className="text-[15px] md:text-[16px] leading-[1.7] text-neutral-700">
                              {pt.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="px-5 md:px-8 py-24 md:py-32 border-t border-neutral-300">
        <div className="mx-auto max-w-2xl text-center flex flex-col items-center">
          <Reveal>
            <Label>Let's begin</Label>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display leading-[1] text-[30px] sm:text-[38px] md:text-[46px]">
              contact me
            </h2>
          </Reveal>
          <Reveal delay={140} className="w-full">
            <ContactForm />
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 md:px-8 py-8 border-t border-neutral-300 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="font-display text-[15px] lowercase">
          kamel jaber<sup className="text-[9px] align-super ml-0.5 text-neutral-400">™</sup>
        </span>
        <Label>&copy; {new Date().getFullYear()} — well-being to well-doing</Label>
      </footer>
    </div>
  );
};

export default Home;
